import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { superAdminPlayerService } from '../../../services/superAdminPlayerService';
import { supabase } from '../../../lib/supabase';
import { validatePlayerAge } from '../../../utils/playerValidation';

const PlayerDetailEditor = ({ playerData, onSave, onClose, loading: parentLoading, onPlayerDataUpdate }) => {
  const [formData, setFormData] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState({});
  const [documentUrls, setDocumentUrls] = useState({});
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (playerData) {
      const player = playerData.player;
      setFormData({
        id: player.id,
        firstName: player.firstName || '',
        middleName: player.middleName || '',
        lastName: player.lastName || '',
        dateOfBirth: player.dateOfBirth ? new Date(player.dateOfBirth).toISOString().split('T')[0] : '',
        mobileNumber: player.mobileNumber || '',
        email: player.email || '',
        mcaId: player.mcaId || '',
        role: player.role || '',
        tshirtSize: player.tshirtSize || '',
        pantSize: player.pantSize || '',
        isCaptain: player.isCaptain || false
      });

      // Load document URLs
      const docs = playerData.documents || [];
      setDocuments(docs);
      loadDocumentUrls(docs);
    }
  }, [playerData]);

  const loadDocumentUrls = async (documents) => {
    const urls = {};
    for (const doc of documents) {
      try {
        const fileName = doc.filePath.replace('player-documents/', '');
        const { data, error } = await supabase.storage
          .from('player-documents')
          .createSignedUrl(fileName, 3600);
        
        if (!error && data) {
          urls[doc.documentType] = data.signedUrl;
        }
      } catch (err) {
        console.error(`Error loading URL for ${doc.documentType}:`, err);
      }
    }
    setDocumentUrls(urls);
  };

  const roleOptions = [
    { value: 'batter', label: 'Batter' },
    { value: 'bowler', label: 'Bowler' },
    { value: 'all_rounder', label: 'All Rounder' },
    { value: 'wicket_keeper', label: 'Wicket Keeper' }
  ];

  const tshirtSizeOptions = [
    { value: 's_36', label: 'S - 36' },
    { value: 'm_38', label: 'M - 38' },
    { value: 'l_40', label: 'L - 40' },
    { value: 'xl_42', label: 'XL - 42' },
    { value: 'xxl_44', label: 'XXL - 44' },
    { value: 'xxxl_46', label: 'XXXL - 46' },
    { value: 'size_26', label: '26' },
    { value: 'size_28', label: '28' },
    { value: 'size_30', label: '30' },
    { value: 'size_32', label: '32' },
    { value: 'size_34', label: '34' },
    { value: 'size_36', label: '36' },
    { value: 'size_38', label: '38' },
    { value: 'size_40', label: '40' },
    { value: 'size_42', label: '42' },
    { value: 'size_44', label: '44' },
    { value: 'size_46', label: '46' }
  ];

  const pantSizeOptions = [
    { value: 'size_28', label: '28' },
    { value: 'size_30', label: '30' },
    { value: 'size_32', label: '32' },
    { value: 'size_34', label: '34' },
    { value: 'size_36', label: '36' },
    { value: 'size_38', label: '38' },
    { value: 'size_40', label: '40' }
  ];

  const documentTypeOptions = [
    { value: 'aadhar_card', label: 'Aadhar Card' },
    { value: 'birth_certificate', label: 'Birth Certificate' },
    { value: 'passport_photo', label: 'Passport Photo' },
    { value: 'school_id_card', label: 'School ID Card' },
    { value: 'bonafide_certificate', label: 'Bonafide Certificate' }
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      // Validate date is not in the future
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      if (birthDate > today) {
        newErrors.dateOfBirth = 'Date of birth cannot be in the future';
      } else {
        // Validate age against registration's age category
        const ageCategory = playerData?.registration?.ageCategory;
        if (ageCategory) {
          const ageError = validatePlayerAge(formData.dateOfBirth, ageCategory);
          if (ageError) {
            newErrors.dateOfBirth = ageError;
          }
        }
      }
    }
    if (!formData.mobileNumber?.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobileNumber?.trim())) {
      newErrors.mobileNumber = 'Invalid mobile number (must be 10 digits)';
    }
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.role) newErrors.role = 'Role is required';
    if (!formData.tshirtSize) newErrors.tshirtSize = 'T-shirt size is required';
    if (!formData.pantSize) newErrors.pantSize = 'Pant size is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSaving(true);
      await onSave(formData);
    } catch (err) {
      console.error('Error saving player:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentUpload = async (documentType, file) => {
    if (!file || !formData?.id) return;

    try {
      setUploadingDocs(prev => ({ ...prev, [documentType]: true }));
      await superAdminPlayerService.uploadPlayerDocument(formData.id, documentType, file);
      
      // Reload player data to get updated documents
      const updated = await superAdminPlayerService.getPlayerDetails(formData.id);
      if (updated && updated.documents) {
        setDocuments(updated.documents);
        loadDocumentUrls(updated.documents);
        // Notify parent to update playerData
        if (onPlayerDataUpdate) {
          onPlayerDataUpdate(updated);
        }
      }
      
      alert('Document uploaded successfully!');
    } catch (err) {
      console.error('Error uploading document:', err);
      alert(`Failed to upload document: ${err.message}`);
    } finally {
      setUploadingDocs(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const handleDocumentDelete = async (documentType) => {
    if (!window.confirm(`Are you sure you want to delete this ${documentType.replace('_', ' ')}?`)) {
      return;
    }

    try {
      setUploadingDocs(prev => ({ ...prev, [documentType]: true }));
      await superAdminPlayerService.deletePlayerDocument(formData.id, documentType);
      
      // Reload player data
      const updated = await superAdminPlayerService.getPlayerDetails(formData.id);
      if (updated && updated.documents) {
        setDocuments(updated.documents);
        loadDocumentUrls(updated.documents);
        // Notify parent to update playerData
        if (onPlayerDataUpdate) {
          onPlayerDataUpdate(updated);
        }
      }
      
      alert('Document deleted successfully!');
    } catch (err) {
      console.error('Error deleting document:', err);
      alert(`Failed to delete document: ${err.message}`);
    } finally {
      setUploadingDocs(prev => ({ ...prev, [documentType]: false }));
    }
  };

  if (!formData) {
    return (
      <div className="bg-white p-12 rounded-lg shadow-sm border border-gray-200 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-gray-600">Loading player details...</p>
      </div>
    );
  }

  const existingDocuments = documents.length > 0 ? documents : (playerData?.documents || []);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Edit Player: {formData.firstName} {formData.lastName}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Registration: {playerData?.registration?.registrationNumber} | 
            Status: <span className="font-medium">{playerData?.registration?.status}</span>
          </p>
        </div>
        <Button variant="outline" onClick={onClose}>
          <Icon name="X" size={16} className="mr-2" />
          Close
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Player Information */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Player Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="First Name"
              value={formData.firstName}
              onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
              error={errors.firstName}
              required
            />
            <Input
              label="Middle Name"
              value={formData.middleName}
              onChange={(e) => setFormData(prev => ({ ...prev, middleName: e.target.value }))}
            />
            <Input
              label="Last Name"
              value={formData.lastName}
              onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
              error={errors.lastName}
              required
            />
            <Input
              label="Date of Birth"
              type="date"
              value={formData.dateOfBirth}
              onChange={(e) => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
              error={errors.dateOfBirth}
              required
            />
            <Input
              label="Mobile Number"
              value={formData.mobileNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, mobileNumber: e.target.value }))}
              error={errors.mobileNumber}
              required
            />
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              error={errors.email}
              required
            />
            <Input
              label="MCA ID"
              value={formData.mcaId}
              onChange={(e) => setFormData(prev => ({ ...prev, mcaId: e.target.value }))}
              placeholder="Optional"
            />
            <Select
              label="Role"
              options={roleOptions}
              value={formData.role}
              onChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              error={errors.role}
              required
            />
            <Select
              label="T-Shirt Size"
              options={tshirtSizeOptions}
              value={formData.tshirtSize}
              onChange={(value) => setFormData(prev => ({ ...prev, tshirtSize: value }))}
              error={errors.tshirtSize}
              required
            />
            <Select
              label="Pant Size"
              options={pantSizeOptions}
              value={formData.pantSize}
              onChange={(value) => setFormData(prev => ({ ...prev, pantSize: value }))}
              error={errors.pantSize}
              required
            />
          </div>
        </div>

        {/* Documents Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents</h3>
          <div className="space-y-4">
            {documentTypeOptions.map((docType) => {
              const existingDoc = existingDocuments.find(d => d.documentType === docType.value);
              const isUploading = uploadingDocs[docType.value];

              return (
                <div key={docType.value} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      {docType.label}
                    </label>
                    {existingDoc && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleDocumentDelete(docType.value)}
                        disabled={isUploading}
                      >
                        <Icon name="Trash2" size={14} className="mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                  {existingDoc ? (
                    <div className="space-y-2">
                      <a
                        href={documentUrls[docType.value]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-2"
                      >
                        <Icon name="File" size={16} />
                        View Document
                      </a>
                      <p className="text-xs text-gray-500">
                        Uploaded: {new Date(existingDoc.uploadedAt).toLocaleString()}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-2">No document uploaded</p>
                  )}
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleDocumentUpload(docType.value, file);
                    }}
                    disabled={isUploading}
                  />
                  {isUploading && (
                    <p className="text-sm text-gray-500 mt-1">Uploading...</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-4 border-t border-gray-200">
          <Button type="submit" disabled={saving || parentLoading}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PlayerDetailEditor;

